import React, { useState, useEffect } from 'react';
import { X, Download, Smartphone } from 'lucide-react';
import { Button } from './ui/button';

export function PWAInstallPrompt() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches 
      || window.navigator.standalone 
      || document.referrer.includes('android-app://');
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if already dismissed
    const dismissed = localStorage.getItem('pwa-install-dismissed');
    if (dismissed) {
      const dismissedDate = new Date(dismissed);
      const now = new Date();
      // Show again after 7 days
      if ((now - dismissedDate) / (1000 * 60 * 60 * 24) < 7) {
        return;
      }
    }

    // Listen for the beforeinstallprompt event (Chrome, Edge, etc.)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show prompt after a short delay to not interrupt initial page load
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // For iOS, show custom prompt after delay
    if (iOS && !standalone) {
      setTimeout(() => setShowPrompt(true), 5000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      await deferredPrompt.userChoice;
      // User responded to install prompt
      setDeferredPrompt(null);
      setShowPrompt(false);
    }
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-install-dismissed', new Date().toISOString());
  };

  // Don't show if already installed
  if (isStandalone || !showPrompt) {
    return null;
  }

  return (
    <div className="fixed bottom-20 left-4 right-4 sm:left-auto sm:right-4 sm:w-80 z-50 animate-in slide-in-from-bottom-4 duration-300">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#F87171] to-[#FB923C] p-4 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <Smartphone className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-semibold">Install Sorta Media</h3>
                <p className="text-xs text-white/80">Add to your home screen</p>
              </div>
            </div>
            <button 
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Dismiss"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          {isIOS ? (
            // iOS instructions (can't auto-prompt)
            <div className="space-y-3">
              <p className="text-sm text-[#64748B]">
                Install this app on your iPhone:
              </p>
              <ol className="text-sm text-[#3D4F6F] space-y-2">
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-[#F87171]/10 text-[#F87171] rounded-full flex items-center justify-center text-xs font-bold">1</span>
                  Tap the Share button <span className="inline-block w-4 h-4 bg-blue-500 text-white text-xs rounded">↑</span>
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-[#F87171]/10 text-[#F87171] rounded-full flex items-center justify-center text-xs font-bold">2</span>
                  Scroll down and tap &ldquo;Add to Home Screen&rdquo;
                </li>
                <li className="flex items-center gap-2">
                  <span className="w-5 h-5 bg-[#F87171]/10 text-[#F87171] rounded-full flex items-center justify-center text-xs font-bold">3</span>
                  Tap &ldquo;Add&rdquo; to install
                </li>
              </ol>
              <Button 
                variant="outline" 
                className="w-full mt-2"
                onClick={handleDismiss}
              >
                Got it
              </Button>
            </div>
          ) : (
            // Android/Desktop install button
            <div className="space-y-3">
              <p className="text-sm text-[#64748B]">
                Get quick access to your watchlists, rankings, and discover new content - even offline!
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={handleInstall}
                  className="flex-1 bg-[#F87171] hover:bg-[#EF4444] text-white"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Install App
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleDismiss}
                >
                  Later
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
